import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Home, Map, Bike, BookOpen, ArrowRight, Compass } from 'lucide-react'
import { SearchBar } from '../../components/ui/SearchBar'
import { useLanguageStore } from '../../lib/store'

export default function NotFoundPage() {
    const { language } = useLanguageStore()

    const quickLinks = [
        { to: '/', icon: Home, label: { en: 'Home', vi: 'Trang chủ' } },
        { to: '/blog', icon: BookOpen, label: { en: 'Blog', vi: 'Bài viết' } },
        { to: '/tours', icon: Map, label: { en: 'Tours', vi: 'Du lịch' } },
        { to: '/vehicles', icon: Bike, label: { en: 'Rentals', vi: 'Thuê xe' } }
    ]

    return (
        <>
            <Helmet>
                <title>404 - {language === 'en' ? 'Page Not Found' : 'Không tìm thấy trang'}</title>
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex items-center justify-center p-4">
                <div className="w-full max-w-5xl">
                    <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                        {/* Left Column - Illustration & Animation */}
                        <div className="order-2 md:order-1 flex flex-col items-center text-center">
                            {/* Animated 404 */}
                            <div className="relative mb-6">
                                <div className="text-[120px] md:text-[180px] font-black text-transparent bg-clip-text bg-gradient-to-br from-primary-400 to-primary-600 leading-none select-none animate-pulse">
                                    404
                                </div>
                                {/* Floating elements */}
                                <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce opacity-80" style={{ animationDelay: '0.2s' }} />
                                <div className="absolute top-1/4 -left-6 w-6 h-6 bg-primary-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0.5s' }} />
                                <div className="absolute bottom-8 right-0 w-4 h-4 bg-teal-400 rounded-full animate-bounce opacity-70" style={{ animationDelay: '0.8s' }} />

                                {/* Compass icon floating */}
                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 animate-spin" style={{ animationDuration: '8s' }}>
                                    <Compass className="w-12 h-12 text-primary-300" />
                                </div>
                            </div>

                            {/* Lost traveler illustration using CSS */}
                            <div className="relative w-48 h-48 mb-4">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full animate-pulse" />
                                <div className="absolute inset-4 bg-white rounded-full shadow-inner flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="text-4xl mb-2">🏝️</div>
                                        <div className="text-xs text-gray-500">
                                            {language === 'en' ? 'Lost at sea?' : 'Lạc đường?'}
                                        </div>
                                    </div>
                                </div>
                                {/* Wave animation */}
                                <div className="absolute -bottom-4 left-0 right-0 h-8 bg-gradient-to-t from-primary-200 to-transparent rounded-b-full opacity-50 animate-pulse" />
                            </div>

                            <p className="text-gray-500 text-sm max-w-xs">
                                {language === 'en'
                                    ? "The page you're looking for has sailed away to unknown waters..."
                                    : "Trang bạn tìm kiếm đã trôi dạt đến vùng biển xa xôi..."}
                            </p>
                        </div>

                        {/* Right Column - Search & Actions */}
                        <div className="order-1 md:order-2">
                            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                    {language === 'en' ? 'Page Not Found' : 'Không tìm thấy trang'}
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    {language === 'en'
                                        ? "Let's help you find your way back to paradise."
                                        : "Hãy để chúng tôi giúp bạn tìm đường về thiên đường."}
                                </p>

                                {/* Search */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {language === 'en' ? 'Search for what you need:' : 'Tìm kiếm điều bạn cần:'}
                                    </label>
                                    <SearchBar
                                        className="w-full"
                                        inputClassName="bg-gray-50 border-gray-200"
                                        placeholder={language === 'en' ? 'Tours, vehicles, articles...' : 'Tour, xe, bài viết...'}
                                    />
                                </div>

                                {/* Quick Links */}
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                                        {language === 'en' ? 'Quick navigation:' : 'Điều hướng nhanh:'}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-3">
                                        {quickLinks.map(link => (
                                            <Link
                                                key={link.to}
                                                to={link.to}
                                                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-primary-50 hover:text-primary-700 transition group"
                                            >
                                                <link.icon className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition" />
                                                <span className="font-medium text-sm">{link.label[language]}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>

                                {/* CTA */}
                                <Link
                                    to="/"
                                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-medium hover:from-primary-700 hover:to-primary-800 transition shadow-lg shadow-primary-200"
                                >
                                    {language === 'en' ? 'Back to Homepage' : 'Về trang chủ'}
                                    <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>

                            {/* Help text */}
                            <p className="text-center text-sm text-gray-400 mt-4">
                                {language === 'en'
                                    ? 'Need assistance? '
                                    : 'Cần hỗ trợ? '}
                                <a href="mailto:support@phuquoc-rental.com" className="text-primary-600 hover:underline">
                                    {language === 'en' ? 'Contact us' : 'Liên hệ'}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
